/*
 * This file is part of the Akershus Eiendom Nettsted 2019 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import FuzzySearch from 'fuzzy-search';
import Input from 'components/Input/Input';
import Magnifier from 'components/Icons/Magnifier/Magnifier';
import Person from 'components/Person/Person';
import { useQueryState } from 'utils/querystringState';
import useEnhanced from 'utils/enhanced';
import './Staff.scss';

/**
 * This is the Staff component.
 *
 * @author Thomas Sømoen <thomas@apt.no>
 *
 * @return {JSX}
 */
function Staff({ title, all, cv, business, search, businesses, employees }) {
  const enhanced = useEnhanced(760);
  const [{ s: frase, f: filter }, setState] = useQueryState({
    s: '',
    f: '',
  });
  const path = encodeURIComponent(`${location.pathname}${location.search}`);

  function toggleFilter(key) {
    if (key === filter) {
      setState(state => ({
        ...state,
        f: '',
      }));
    } else {
      setState(state => ({
        ...state,
        f: key,
      }));
    }
  }

  function filterEmployees() {
    const filtered = employees
      .filter(({ businesses }) => {
        if (filter) {
          return businesses.indexOf(filter) !== -1;
        }

        return true;
      })
      .map(({ businesses: localBusinesses, url, ...props }) => ({
        ...props,
        businesses: (localBusinesses || []).map(key => businesses[key]),
        businessNames: (localBusinesses || []).map(key => businesses[key].name),
        url: `${url}?b=${path}`,
      }))
      .sort((a, b) => {
        if (a.name < b.name) return -1;
        if (a.name > b.name) return 1;
        return 0;
      });
    if (frase) {
      const searcher = new FuzzySearch(
        filtered,
        ['name', 'role', 'businessNames'],
        { sort: true }
      );
      return searcher.search(frase);
    }

    return filtered;
  }

  function renderFilter() {
    if (enhanced) {
      return (
        <>
          <button
            className={classnames({ active: filter === '' })}
            onClick={() => {
              setState(state => ({
                ...state,
                f: '',
              }));
            }}
          >
            <span
              className="filter-item animate-stagger"
              style={{ animationDelay: `${0.08}s` }}
            >
              {all}
            </span>
          </button>
          {Object.keys(businesses).map((key, i) => (
            <button
              className={classnames({
                active: key === filter,
              })}
              key={key}
              onClick={() => {
                toggleFilter(key);
              }}
            >
              <span
                className="filter-item animate-stagger"
                style={{ animationDelay: `${(i + 1) * 0.08}s` }}
              >
                {businesses[key].name}
              </span>
            </button>
          ))}
        </>
      );
    }

    return (
      <select
        value={filter}
        onChange={({ target: { value } }) => {
          setState(state => ({
            ...state,
            f: value,
          }));
        }}
      >
        <option value="">{all}</option>
        {Object.keys(businesses).map(key => (
          <option key={key} value={key}>
            {businesses[key].name}
          </option>
        ))}
      </select>
    );
  }

  const filteredEmployees = filterEmployees();

  return (
    <div className="staff-component">
      <header>
        <h1 className="animate-1">
          <strong>
            {filteredEmployees.filter(employee => !employee.external).length}
          </strong>{' '}
          {title}
        </h1>
        <Input
          className="search-input animate-2"
          icon={<Magnifier />}
          placeholder={search}
          value={frase}
          onChange={({ target: { value } }) => {
            setState(state => ({
              ...state,
              s: value,
            }));
          }}
        />
      </header>
      <div className="filter">{renderFilter()}</div>
      <div className="employees">
        {filteredEmployees.map(({ id, ...props }, i) => (
          <Person
            className="animate-stagger"
            key={id}
            cv={cv}
            business={business}
            style={{ animationDelay: `${i * 0.08}s` }}
            {...props}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Staff.propTypes = {
  title: PropTypes.string.isRequired,
  all: PropTypes.string.isRequired,
  cv: PropTypes.string.isRequired,
  business: PropTypes.string.isRequired,
  search: PropTypes.string.isRequired,
  businesses: PropTypes.object.isRequired,
  employees: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      role: PropTypes.string.isRequired,
      businesses: PropTypes.array.isRequired,
      url: PropTypes.string.isRequired,
      external: PropTypes.bool,
    })
  ).isRequired,
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Staff.defaultProps = {
  title: 'Staff',
  all: 'Show all',
  cv: 'Show CV',
  business: 'Business areas',
  search: 'Search for name / title / business',
  businesses: {},
  employees: [],
};

export default Staff;
